import { RoundsFS } from "src/app/domain/fund-strategy/pojo/fund-strategy-export-classes";
import { Rows } from "../../../interfaces";
import { dashInsteadOfNullCommon, formats, getCell, withCellStyles } from "../../../utils";
import { getHeadersForDisplay, getHeadersForLogic } from "./headers";
import { boldGrayBgLeftGrandTotal, left, leftIntend } from "src/app/util/excelStyles";

export const getPreparedDataInternal = (data: RoundsFS[]): Rows => {
  const newData: Rows = [];
  newData.push(getHeadersForDisplay());
  const mainRows = getMainRows(data);
  newData.push(...mainRows);
  const totalRow = getTotalRow();
  newData.push(...totalRow);
  currentRowIndex = 2;
  return newData;
};

const getMainRows = (data: RoundsFS[]): Rows => {
  const rows: Rows = [];
  const headers = getHeadersForLogic();
  const mappedToHeaders = data.map((r) => getNewMapping(r));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      if (header === 'Round name') {
        row.push(withCellStyles(mapped[header], leftIntend));
      } else {
        row.push(withCellStyles(mapped[header], left));
      }
    }
    rows.push(row);
  }
  return rows;
};

const getNewMapping = (round: RoundsFS): object => {
  const body = {
    "Round name": round.name,
    "ESOP, %": getCell({
      v: round.esop / 100,
      z: formats.percents,
      t: 'n',
    }),
    "Pre-money": getCell({
      v: round.preMoney,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Round": getCell({
      v: round.round,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Post-money": getCell({
      f: `C${currentRowIndex}+D${currentRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Investment (Companies)": getCell({
      v: dashInsteadOfNullCommon(round.companiesNumber),
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    "Investment (Amount)": getCell({
      v: dashInsteadOfNullCommon(round.investment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Holding (%)": getCell({
      v: dashInsteadOfNullCommon(round.holding),
      z: formats.percents,
      t: 'n',
    }),
    "Exit. comp": getCell({
      v: dashInsteadOfNullCommon(round.exitCompNumber),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Comp. value": getCell({
      v: dashInsteadOfNullCommon(round.exitCompPrice),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceeds": getCell({
      v: dashInsteadOfNullCommon(round.proceeds),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Write-off (Companies)": getCell({
      v: dashInsteadOfNullCommon(round.writeOff),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Investment (Total)": getCell({
      v: dashInsteadOfNullCommon(round.totalInvestment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceed (Total)": getCell({
      v: dashInsteadOfNullCommon(round.exitProceeds),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "MOIC (Total)": null,
    'empty': null,
    'empty(1)': null,
    'empty(2)': null,
    'empty(3)': null,
  };
  currentRowIndex++;
  return body;
};

const getTotalMapping = (): any => {
  const body = {
    "Round name": 'Total',
    "ESOP, %": getCell({
      v: '',
      z: formats.percents,
      t: 'n',
    }),
    "Pre-money": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Round": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Post-money": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Investment (Companies)": getCell({
      f: `MAX(G2:G${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    "Investment (Amount)": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Holding (%)": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Exit. comp": getCell({
      f: `SUM(K2:K${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Comp. value": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceeds": getCell({
      v: '',
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Write-off (Companies)": getCell({
      f: `SUM(O2:O${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Investment (Total)": getCell({
      f: `SUM(Q2:Q${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "Proceed (Total)": getCell({
      f: `SUM(R2:R${currentRowIndex - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    "MOIC (Total)": getCell({
      f: `IFERROR(R${currentRowIndex}/Q${currentRowIndex},0)`,
      z: formats.multiplier,
      t: 's',
    }),
    'empty': null,
    'empty(1)': null,
    'empty(2)': null,
    'empty(3)': null,
  };
  currentRowIndex++;
  return [body];
};

const getTotalRow = (): Rows => {
  const rows: Rows = [];
  const headers = getHeadersForDisplay();
  const mappedToHeaders = getTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];

    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldGrayBgLeftGrandTotal));
    }
    rows.push(row);
  }
  return rows;
};
let currentRowIndex = 2;

